import { useCallback } from 'react'
import { useSendCommand } from './TelephonyProvider'
import { useToast } from '../ui/Toast'
import {
  ReadyCommands, isCommandError, type CommandResponse, type CommandErrorResult
} from '../contract'
import type { SendCommand } from '../realtime/types'

// Wraps the raw transport sendCommand with result handling: surfaces error_code/error_message
// as a toast instead of silently dropping it, and gives a friendly message for the pending
// (PARAM_INVALID) edge handlers. Optionally shows a success confirmation.

export interface CommandOptions {
  /** Confirmation toast on success (omit for silent success). */
  successMessage?: string
}

/** tel_make_call -> "Make call" for human-readable pending-handler messages. */
function commandLabel(cmd: string): string {
  const base = cmd.replace(/^tel_/, '').replace(/_/g, ' ')
  return base.charAt(0).toUpperCase() + base.slice(1)
}

export type RunCommand = (cmd: SendCommand, opts?: CommandOptions) => Promise<CommandResponse | null>

export function useCommand(): RunCommand {
  const send = useSendCommand()
  const { push } = useToast()
  return useCallback<RunCommand>(async (cmd, opts = {}) => {
    try {
      const res = await send(cmd)
      if (isCommandError(res)) {
        const err = res.result as CommandErrorResult
        if (err.error_code === 'PARAM_INVALID' && !ReadyCommands.has(cmd.command)) {
          push(`${commandLabel(cmd.command)} isn't wired up on the backend yet.`, 'warn')
        } else {
          push(err.error_message || err.error_code || 'Command failed.', 'error')
        }
        return res
      }
      if (opts.successMessage) push(opts.successMessage, 'success')
      return res
    } catch (e) {
      push(`Command failed: ${(e as Error)?.message ?? 'network error'}`, 'error')
      return null
    }
  }, [send, push])
}
